import Analytics from '@streem/analytics';
import { StreemAPI, StreemApiDeviceSupportedWallPlatforms } from '@streem/api';
import StreemAuth, { isExpertUser, isLoginIdentityUser } from '@streem/auth';
import { AppId, RoomId, UserId } from '@streem/domain-id';
import { contextValues } from '@streem/logger';
import { configureTwilioWallLogger } from '@streem/sdk-twilio-wall';
import { configureChimeVideo } from '@streem/sdk-chime-video';
import { BehaviorSubject } from 'rxjs';
import { GeolocationController } from './data/controllers/geolocation_controller';
import { DetailSession } from './data/session/detail_session';
import { UserSession } from './data/session/user_session';
import { StreemError } from './streem_error';
import { requestTabLeadership } from './util/active_tab_election';
import { determineSupportedVideoCodecs } from './util/determine_supported_video_codecs';
import { determineSupportedVideoPlatforms } from './util/determine_supported_video_platforms';
import { generateNewIdentifier, retrieveIdentifier } from './util/dom/storage';
import { logger, setLogger } from './util/logger';
class Streem {
    constructor() {
        this.configuredSubject = new BehaviorSubject(false);
        this.configured = this.configuredSubject.asObservable();
        this.userSessionSubject = new BehaviorSubject(undefined);
        this.userSession = this.userSessionSubject.asObservable();
        this.isLeaderSessionSubject = new BehaviorSubject(false);
        this.isLeaderSession = this.isLeaderSessionSubject.asObservable();
        this.handleAuthCallback = async (authUser) => {
            this.clearUserSession();
            // Do not try to register a non-user or LoginIdentityUser.
            if (!authUser || isLoginIdentityUser(authUser)) {
                return;
            }
            const userId = new UserId(authUser.id);
            const deviceId = await this.registerDeviceAndIdentifyUser(authUser);
            await this.startUserSession(userId, deviceId);
        };
    }
    /**
     * Configure the Streem SDK
     */
    configure(config) {
        if (!config || !config.appId) {
            throw new StreemError('appId is required to initialize Streem');
        }
        this.appVersion = config.appVersion || '1'; // '1' is expected to be used for localhost only.
        GeolocationController.GoogleMapsKey = config.googleMapsKey;
        setLogger(config.logger);
        configureTwilioWallLogger(config.logger.extend('sdk-twilio-wall'));
        configureChimeVideo({ logger: config.logger.extend('sdk-chime-video') });
        requestTabLeadership().then(() => {
            this.isLeaderSessionSubject.next(true);
            logger.info('Leader Streem tab set');
        });
        try {
            this.appId = new AppId(config.appId);
            StreemAuth.onAuthStateChanged(this.handleAuthCallback);
            this.configuredSubject.next(true);
        }
        catch (e) {
            logger.error('failed to configure Streem SDK', e);
            throw new StreemError('Internal Error');
        }
    }
    async startAnonymousDetailSession(roomSid, shareToken) {
        if (!this.configuredSubject.value) {
            throw new Error('Streem must be configured before starting a session');
        }
        StreemAPI.setShareToken(shareToken);
        const deviceId = await this.registerDevice();
        const detailSession = await DetailSession.initialize({
            roomId: new RoomId(roomSid),
            deviceId,
            readOnly: true,
        });
        await detailSession.connect();
        return detailSession;
    }
    async startUserSession(userId, deviceId) {
        const session = await UserSession.initialize({
            userId,
            deviceId,
        });
        await session.connect();
        this.userSessionSubject.next(session);
    }
    /**
     * Identify the user to platform
     */
    async registerDeviceAndIdentifyUser(user) {
        try {
            const deviceId = await this.registerDevice();
            const userMeta = {
                deviceSid: deviceId.toString(),
                isExpert: isExpertUser(user),
            };
            if (isExpertUser(user) || isLoginIdentityUser(user)) {
                userMeta.name = user.name;
                userMeta.avatarUrl = user.avatarUrl;
                userMeta.email = user.email;
            }
            await StreemAPI.users.identifyUser(user.id, userMeta);
            logger.debug(`Identifying user ${user.id} with device ${deviceId}`);
            return deviceId;
        }
        catch (e) {
            logger.error('Failed to register device and identify user', e);
            throw new StreemError('Internal Error');
        }
    }
    async registerDevice(deviceIdOverride) {
        try {
            const deviceCountry = () => {
                const localSplit = navigator.language.split('-') || [];
                return localSplit[1];
            };
            const localId = deviceIdOverride || retrieveIdentifier();
            const deviceInfo = {
                sid: localId.toString(),
                appSid: this.appId.toString(),
                appVersion: this.appVersion,
                deviceCountry: deviceCountry(),
                deviceLocale: navigator.languages[0],
                // eslint-disable-next-line new-cap
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                platform: 'web',
                userAgent: window.navigator.userAgent,
                supportedVideoCodecs: determineSupportedVideoCodecs(),
                supportedVideoPlatforms: determineSupportedVideoPlatforms(),
                supportedWallPlatforms: [
                    StreemApiDeviceSupportedWallPlatforms.TWILIO,
                    StreemApiDeviceSupportedWallPlatforms.STREEM,
                ],
            };
            await StreemAPI.devices.updateDevice(localId.toString(), {
                device: deviceInfo,
            });
            Analytics.setDeviceId(localId);
            logger.setContextValue(contextValues.DEVICE_INFO, deviceInfo);
            logger.debug(`Device registered with id: ${localId}`);
            return localId;
        }
        catch (error) {
            // TODO look into addressing the fact that you can get into a state where your deviceId is already in use
            // see: https://streempro.atlassian.net/browse/STREEM-2896
            if (error.status === 409 && !deviceIdOverride) {
                try {
                    const type = sessionStorage.getItem('appType');
                    const typeApp = type != null ? type : 'ev2';
                    await this.registerDevice(generateNewIdentifier(typeApp));
                }
                catch (e) {
                    logger.error('Failed to register device', e);
                    throw new StreemError('Internal Error');
                }
            }
            throw error;
        }
    }
    clearUserSession() {
        if (this.userSessionSubject.value) {
            this.userSessionSubject.value.disconnect();
            this.userSessionSubject.next(undefined);
        }
    }
}
// This is a singleton, default export is valid here.
// eslint-disable-next-line import/no-default-export
export default new Streem();
//# sourceMappingURL=streem.js.map